import { useMemo, useState } from 'react'
import { Banner, Button, Checkbox, Form, Heading } from '@boltenergy-be/design-system'
import { FlowAlternatives, InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import { Language } from 'types/language'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import { FormProvider, useForm } from 'react-hook-form'
import { SubscribeFormInputs } from 'features/simulation/steps/types'
import { Producer } from 'types/producer'
import { SubscribeFormProps } from 'features/simulation/components/subscribe-form/types'
import { updateOpportunityId } from 'store/app/slice'
import { useGetProducersQuery } from 'store/api/bolt-api'
import { toggleContactMe, updateAllInputs } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { identifyUser, setPostHogPersonProperties, trackGTMEvent } from 'utils/tracking'
import { log, setLogUser } from 'utils/logging'
import { Flow } from 'types/logging'
import { AxiosError } from 'axios'
import { BoltApiResponseBody } from 'types/bolt-api/request'
import { Email, FirstName, LastName, Phone } from 'components/inputs'
import boltBackend from 'lib/bolt-backend'
import { parseCreateProspectData } from 'lib/bolt-backend/registrations/upsertProspect.utils'
import { ERROR_CODES } from '@boltenergy-be/shared'

const SubscribeForm = ({ finishSubmission, product }: SubscribeFormProps) => {
  // Redux store
  const { selectedProducer } = useStoreSelector((store) => store.app)
  const { inputs, flowAlternative } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // RTK queries
  const { data: producers } = useGetProducersQuery()

  // i18n
  const { t } = useTranslation(['simulation', 'commonFormFields', 'common'])

  // Router
  const { locale } = useRouter()

  // REACT HOOK FORM
  const hookForm = useForm<SubscribeFormInputs>({
    defaultValues: {
      contactMoment: inputs.personalData.contactMoment,
      email: inputs.personalData.email,
      firstName: inputs.personalData.firstName,
      lastName: inputs.personalData.lastName,
      mobilePhone: inputs.personalData.mobilePhone
    },
    mode: 'onChange'
  })
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    register
  } = hookForm

  // Constants
  const producer = useMemo<Producer>(() => producers?.[selectedProducer], [producers, selectedProducer])
  const isLeadGenCampaign = flowAlternative[FlowAlternatives.TELESALES_CAMPAIGN]

  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Handles the form submit after validation by React Hook Form
   *
   * @returns {Promise<void>}
   */
  const onSubmit = async (data: SubscribeFormInputs): Promise<void> => {
    setError(null)
    setIsLoading(true)

    try {
      const { firstName, lastName, email, mobilePhone } = data

      setPostHogPersonProperties({ email })

      setLogUser({ email, name: `${firstName} ${lastName}` })

      // Update the fields in the registration state
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            [InputGroupKeys.PERSONAL_DATA]: {
              ...inputs.personalData,
              [PersonalDataFieldKeys.FIRSTNAME]: firstName,
              [PersonalDataFieldKeys.LASTNAME]: lastName,
              [PersonalDataFieldKeys.EMAIL]: email,
              [PersonalDataFieldKeys.MOBILE_PHONE]: mobilePhone,
              [PersonalDataFieldKeys.CONTACT_MOMENT]: undefined // showContactMoment ? contactMoment ?? ContactMoment.MORNING : undefined
            }
          }
        })
      )

      // Subscribe the lead
      const parsedSubscribeData = parseCreateProspectData({
        customProduct: product,
        language: locale as Language,
        producer,
        startedFulfillmentFlow: false
      })
      const { success, data: createProspectData, message } = await boltBackend.upsertProspect(parsedSubscribeData)

      // Show an error message if the request was unsuccessful
      if (!success) {
        setError(message ?? 'error')
        return
      }

      const { opportunityId } = createProspectData

      dispatch(updateOpportunityId({ opportunityId }))
      identifyUser(opportunityId, { email })

      // Track 'HotLead' event @Semetis
      trackGTMEvent('HotLead', { email })

      dispatch(toggleContactMe())

      finishSubmission()
    } catch (error) {
      setError((error as AxiosError<BoltApiResponseBody>)?.response?.data?.message)
      log({ error, identifier: `[${Flow.SIMULATION}:createProspect]` })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Heading as="h2" variant="h3">
        {isLeadGenCampaign ? t('leadGenCampaign.subscribe.title') : t('subscribe.title')}
      </Heading>
      <p>{isLeadGenCampaign ? t('leadGenCampaign.subscribe.description') : t('subscribe.description')}</p>

      <FormProvider {...hookForm}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FirstName />

          <LastName />

          <Email grid="full" />

          <Phone grid="full" />

          <Checkbox
            {...register('permission', { required: t('validation:required') })}
            className="grid-col-full"
            id="permission"
            label={t('common:subscribePermission')}
          />

          <Form.Footer>
            <Button disabled={!!error || !isDirty || !isValid} loading={isLoading}>
              {t('common:send')}
            </Button>

            {error && (
              <Banner
                className="mt-500"
                type="blocking"
                title={error === ERROR_CODES.alreadyCustomer ? t('common:errors.alreadyCustomer') : t('common:error')}
              />
            )}
          </Form.Footer>
        </Form>
      </FormProvider>
    </>
  )
}

export default SubscribeForm
